import { type Ref, readonly, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { closeModal, openModal } from '@ds/components/ModalBase'

import headerTranslations from '../pages/components/Header/Header.translations'
import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'

import type {
  NewChatDialog,
  WebShoppingAssistantHost,
} from './useShoppingAssistantHost.injection-key'

// Single RevDialog instance shared by both the new-chat and close confirmations.
export const WEB_SIDEBAR_CONFIRM_DIALOG_NAME =
  'shopping-assistant-web-sidebar-confirm'

/**
 * Builds the host adapter for the web sidebar shell (FAB + drawer). Close and
 * new-chat are confirmed via a RevDialog (gated on a non-empty conversation),
 * close hides the panel through the presence store, and cart/url/ready
 * concerns are no-ops on the web (the front-office cart updates reactively
 * in-app; there is no chat URL in v1).
 *
 * Returns the host plus the bits the container component needs to render the
 * confirmation dialog, to inject a message into a live conversation, and the
 * synced chatId (for pushing browsing context once a chat exists).
 */
export function useWebSidebarShoppingAssistantHost(): {
  host: WebShoppingAssistantHost
  currentDialog: Ref<NewChatDialog | null>
  acceptDialog: () => void
  declineDialog: () => void
  chatId: Readonly<Ref<string | null>>
} {
  const store = useShoppingAssistantWebSidebar()
  const i18n = useI18n()

  const receivedEvent = ref('')
  const currentDialog = ref<NewChatDialog | null>(null)
  const chatId = ref<string | null>(null)

  // The chat is being replaced (user-initiated new chat) or the panel closed:
  // the old chatId is no longer valid until synced again.
  watch(
    () => store.conversationKey,
    () => {
      chatId.value = null
    },
  )
  watch(
    () => store.isOpen,
    (isOpen) => {
      if (!isOpen) chatId.value = null
    },
  )

  // Single-slot dialog: a request while one is already pending is dropped
  // rather than silently swapping the pending dialog's onAccept/onDecline
  // under the user (e.g. a reactive seeded-launch confirm racing a manual
  // "new chat" click). The user's in-flight choice always wins.
  function confirm(dialog: NewChatDialog) {
    if (currentDialog.value) {
      return
    }

    currentDialog.value = dialog
    openModal(WEB_SIDEBAR_CONFIRM_DIALOG_NAME)
  }

  function acceptDialog() {
    const dialog = currentDialog.value
    closeModal(WEB_SIDEBAR_CONFIRM_DIALOG_NAME)
    currentDialog.value = null
    dialog?.onAccept()
  }

  function declineDialog() {
    const dialog = currentDialog.value
    closeModal(WEB_SIDEBAR_CONFIRM_DIALOG_NAME)
    currentDialog.value = null
    dialog?.onDecline?.()
  }

  const host: WebShoppingAssistantHost = {
    receivedEvent: readonly(receivedEvent),
    clearReceivedEvent: () => {
      receivedEvent.value = ''
    },
    close() {
      if (store.hasUserMessages) {
        confirm({
          title: i18n(headerTranslations.newConversationDialogTitle),
          description: i18n(
            headerTranslations.newConversationDialogDescription,
          ),
          acceptButtonText: i18n(headerTranslations.closeChat),
          declineButtonText: i18n(
            headerTranslations.newConversationDialogNegativeButton,
          ),
          onAccept: () => store.close(),
        })

        return
      }

      store.close()
    },
    newChat(dialog) {
      // Plus button: confirm only when there's a real conversation to lose.
      if (store.hasUserMessages) {
        confirm(dialog)

        return
      }

      dialog.onAccept()
    },
    reportConversation: (value: boolean) => {
      store.setHasUserMessages(value)
    },
    syncChatId: (id: string) => {
      chatId.value = id
    },
  }

  return {
    host,
    currentDialog,
    acceptDialog,
    declineDialog,
    chatId: readonly(chatId),
  }
}
